import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { 
  ArrowLeft, MessageSquare, CheckCircle, Send
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function BetaSurvey() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const loadSurvey = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/beta/v2/survey`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSurvey(res.data);
      } catch (error) {
        if (error.response?.status === 400) {
          toast.info(error.response.data.detail);
          navigate('/beta/status');
        } else {
          toast.error(error.response?.data?.detail || 'Failed to load survey');
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadSurvey();
    }
  }, [user, navigate]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/beta/v2/survey`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurvey(res.data);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info(error.response.data.detail);
        navigate('/beta/status');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load survey');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length < 5) {
      toast.error(`Please answer all 5 questions (${Object.keys(answers).length}/5 answered)`);
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/beta/v2/survey/response`, {
        answers,
        comment: comment.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResult(res.data);
      setSubmitted(true);
      toast.success('Survey submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Button onClick={() => navigate('/auth')}>Sign In</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Success screen
  if (submitted && result) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b-2 border-black sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
            <Sidebar />
            <h1 className="text-xl font-bold">Beta Survey</h1>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-white border-2 border-black rounded-xl p-8 shadow-brutal text-center">
            <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            
            <h2 className="text-2xl font-bold mb-2">
              {result.is_complete ? '🎉 Beta Complete!' : 'Thank You!'}
            </h2>
            
            <p className="text-slate-600 mb-6">
              {result.is_complete 
                ? "You've completed all 60 questions and earned free access for the rest of the year!"
                : "Your feedback helps us build a better community."
              }
            </p>

            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-indigo-500">{result.questions_completed}</div>
                  <div className="text-sm text-slate-500">Questions completed</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-emerald-500">{result.questions_remaining}</div>
                  <div className="text-sm text-slate-500">Remaining</div>
                </div>
              </div>
              <div className="mt-4">
                <div className="bg-slate-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="h-full bg-indigo-500 rounded-full transition-all"
                    style={{ width: `${result.completion_percentage}%` }}
                  />
                </div>
                <p className="text-sm text-slate-500 mt-1">{result.completion_percentage}% complete</p>
              </div>
            </div>

            <Button
              onClick={() => navigate('/beta/status')}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black"
            >
              View Beta Status
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-4">No survey available</h2>
          <Button onClick={() => navigate('/beta/status')}>Back to Beta Status</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/beta/status')} className="hover:bg-slate-100 p-2 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">{survey.title}</h1>
              <p className="text-sm text-slate-500">Week {survey.week_number} • {survey.questions_completed}/60 completed</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-brutal">
          {/* Progress indicator */}
          <div className="flex items-center justify-between mb-6 text-sm">
            <span className="text-slate-500">Answer all 5 questions</span>
            <span className="font-bold text-indigo-500">{Object.keys(answers).length}/5</span>
          </div>

          {/* Questions */}
          <div className="space-y-8">
            {survey.questions.map((question, idx) => (
              <div key={question.question_id} className="pb-6 border-b border-slate-100 last:border-0">
                <p className="font-medium mb-4">
                  {idx + 1}. {question.prompt}
                </p>
                <RadioGroup
                  value={answers[question.question_id] || ''}
                  onValueChange={(value) => handleAnswerChange(question.question_id, value)}
                  className="space-y-2"
                >
                  {question.options.map((option, optIdx) => (
                    <div key={optIdx} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <RadioGroupItem 
                        value={option} 
                        id={`${question.question_id}-${optIdx}`}
                        className="border-slate-300"
                      />
                      <Label 
                        htmlFor={`${question.question_id}-${optIdx}`}
                        className="flex-1 cursor-pointer text-sm"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}
          </div>

          {/* Optional comment */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <Label className="text-sm font-medium">
              Anything else on your mind? (optional)
            </Label>
            <Textarea
              placeholder="Share additional thoughts, suggestions, or feedback..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="mt-2 resize-none"
              rows={3}
            />
          </div>

          {/* Submit */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSubmit}
              disabled={submitting || Object.keys(answers).length < 5}
              className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black px-8"
            >
              {submitting ? 'Submitting...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Survey
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
